import React, { useState } from 'react';
import { Grid, TextField, Button, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { validatePassword } from '../../../../utils/validation/passwordValidation';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';

export default function InitialSignupForm({ initialData, onSubmit }) {
  const [formData, setFormData] = useState({
    email: initialData?.email || '',
    password: initialData?.password || '',
    confirmPassword: initialData?.confirmPassword || '',
  });

  const [errors, setErrors] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { t } = useTranslation();

  const validateForm = () => {
    const newErrors = {
      email: '',
      password: '',
      confirmPassword: ''
    };

    // Email validation
    if (!formData.email) {
      newErrors.email = t(StringKeys.PLEASE_FILL_OUT_THIS_FIELD);
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t(StringKeys.INVALID_EMAIL_PLEASE_TRY_AGAIN);
    }

    // Password validation
    const passwordValidation = validatePassword(formData.password, t);
    if (!formData.password) {
      newErrors.password = t(StringKeys.PLEASE_FILL_OUT_THIS_FIELD);
    } else if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors[0];
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t(StringKeys.PLEASE_FILL_OUT_THIS_FIELD);
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t(StringKeys.PASSWORDS_DO_NOT_MATCH);
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        email: formData.email,
        password: formData.password,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            autoFocus
            margin="dense"
            id="email"
            label={t(StringKeys.EMAIL)}
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, email: e.target.value }));
              setErrors(prev => ({ ...prev, email: '' }));
            }}
            error={!!errors.email}
            helperText={errors.email}
            required
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            margin="dense"
            id="password"
            label={t(StringKeys.PASSWORD)}
            type={showPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={formData.password}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, password: e.target.value }));
              setErrors(prev => ({ ...prev, password: '' }));
            }}
            error={!!errors.password}
            helperText={errors.password}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            margin="dense"
            id="confirmPassword"
            label={`${t(StringKeys.CONFIRM)} ${t(StringKeys.PASSWORD)}`}
            type={showConfirmPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={formData.confirmPassword}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
              setErrors(prev => ({ ...prev, confirmPassword: '' }));
            }}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        <Grid item xs={12}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            sx={{ 
              textTransform: 'none', 
              fontWeight: '600',
              height: '56px',
              fontSize: '16px',
              borderRadius: '30px',
              boxShadow: '0 4px 12px rgba(57, 172, 231, 0.2)',
              mt: 2,
              '&:hover': {
                  boxShadow: '0 6px 16px rgba(57, 172, 231, 0.3)',
              },
              '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
              fullWidth
            >
            {t(StringKeys.NEXT)}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
} 